/**
 * Comprehensive compiled-in setting defaults, generated from PyMOL's
 * `packages/engine/layer1/SettingInfo.h` (the `REC_*` macro table). One entry
 * per non-blank setting, holding the upstream default value:
 *
 *   - boolean (`REC_b`)  -> 0 | 1
 *   - int     (`REC_i`)  -> the default int (min/max args dropped)
 *   - float   (`REC_f`)  -> the default float
 *   - float3  (`REC_3`)  -> the [x,y,z] vector (get_setting_tuple/text read the
 *                            triple; get_setting_float on a float3 returns 0)
 *   - string  (`REC_s`)  -> the default string, verbatim
 *   - color   (`REC_c`)  -> the raw default color reference string ("-1", "red",
 *                            "0x000000", …); `resolveSetting` runs it through
 *                            `getColorIndex` so reads return the color INDEX.
 *
 * Consulted by `resolveSetting` (`cmd/settings2.ts`) as the final fallback when
 * a setting has no per-object override and no value in the executive's live
 * store, so `get_setting_int/float/boolean/text(name)` returns PyMOL's real
 * default for EVERY setting instead of a bare 0.
 *
 * DO NOT EDIT BY HAND — regenerate with `node scripts/gen-setting-defaults.mjs`.
 */

/** Colour-typed setting default: a raw colour reference resolved at read time. */
export interface ColorDefault {
  readonly color: string;
}

export const SETTING_INFO_DEFAULTS: Readonly<Record<string, number | string | number[] | ColorDefault>> = {
  "bonding_vdw_cutoff": 0.2,
  "min_mesh_spacing": 0.6,
  "dot_density": 2,
  "dot_mode": 0,
  "solvent_radius": 1.4,
  "sel_counter": 0,
  "bg_rgb": { color: "0x000000" },
  "ambient": 0.14,
  "direct": 0.45,
  "reflect": 0.45,
  "light": [-0.4, -0.4, -1],
  "power": 1,
  "antialias": 1,
  "cavity_cull": 10,
  "gl_ambient": 0.12,
  "single_image": 0,
  "movie_delay": 30,
  "ribbon_power": 2,
  "ribbon_power_b": 0.5,
  "ribbon_sampling": 1,
  "ribbon_radius": 0,
  "stick_radius": 0.25,
  "hash_max": 100,
  "orthoscopic": 0,
  "spec_reflect": -1,
  "spec_power": -1,
  "sweep_angle": 20,
  "sweep_speed": 0.75,
  "dot_hydrogens": 1,
  "dot_radius": 0,
  "ray_trace_frames": 0,
  "cache_frames": 0,
  "trim_dots": 1,
  "cull_spheres": 0,
  "test1": 3,
  "test2": -0.5,
  "surface_best": 0.25,
  "surface_normal": 0.5,
  "surface_quality": 0,
  "surface_proximity": 1,
  "normal_workaround": 0,
  "stereo_angle": 2.1,
  "stereo_shift": 2,
  "line_smooth": 1,
  "line_width": 1.49,
  "half_bonds": 0,
  "stick_quality": 8,
  "stick_overlap": 0.2,
  "stick_nub": 0.7,
  "all_states": 0,
  "pickable": 1,
  "auto_show_lines": 1,
  "idle_delay": 1.5,
  "no_idle": 2000,
  "fast_idle": 10000,
  "slow_idle": 40000,
  "rock_delay": 30,
  "dist_counter": 0,
  "dash_length": 0.15,
  "dash_gap": 0.45,
  "auto_zoom": -1,
  "overlay": 0,
  "text": 0,
  "button_mode": 0,
  "valence": 1,
  "nonbonded_size": 0.25,
  "label_color": { color: "-6" },
  "ray_trace_fog": -1,
  "spheroid_scale": 1,
  "ray_trace_fog_start": -1,
  "spheroid_smooth": 1.1,
  "spheroid_fill": 1.3,
  "auto_show_nonbonded": 1,
  "cache_display": 1,
  "mesh_radius": 0,
  "backface_cull": 0,
  "gamma": 1,
  "dot_width": 2,
  "auto_show_selections": 1,
  "auto_hide_selections": 1,
  "selection_width": 3,
  "selection_overlay": 1,
  "static_singletons": 1,
  "depth_cue": 1,
  "specular": 1,
  "shininess": 55,
  "sphere_quality": 1,
  "fog": 1,
  "isomesh_auto_state": 0,
  "mesh_width": 1,
  "cartoon_sampling": -1,
  "cartoon_loop_radius": 0.2,
  "cartoon_loop_quality": -1,
  "cartoon_power": 2,
  "cartoon_power_b": 0.52,
  "cartoon_rect_length": 1.4,
  "cartoon_rect_width": 0.4,
  "internal_gui_width": 220,
  "internal_gui": 1,
  "cartoon_oval_length": 1.35,
  "cartoon_oval_width": 0.25,
  "cartoon_oval_quality": -1,
  "cartoon_tube_radius": 0.5,
  "cartoon_tube_quality": -1,
  "cartoon_debug": 0,
  "ribbon_width": 3,
  "dash_width": 2.5,
  "dash_radius": 0,
  "cgo_ray_width_scale": -0.15,
  "line_radius": 0,
  "cartoon_round_helices": 1,
  "cartoon_refine_normals": -1,
  "cartoon_flat_sheets": 1,
  "cartoon_smooth_loops": 0,
  "cartoon_dumbbell_length": 1.6,
  "cartoon_dumbbell_width": 0.17,
  "cartoon_dumbbell_radius": 0.16,
  "cartoon_fancy_helices": 0,
  "cartoon_fancy_sheets": 1,
  "ignore_pdb_segi": 0,
  "ribbon_throw": 1.35,
  "cartoon_throw": 1.35,
  "cartoon_refine": 5,
  "cartoon_refine_tips": 10,
  "cartoon_discrete_colors": 0,
  "normalize_ccp4_maps": 1,
  "surface_poor": 0.85,
  "internal_feedback": 1,
  "cgo_line_width": 1,
  "cgo_line_radius": -0.05,
  "logging": 0,
  "robust_logs": 0,
  "log_box_selections": 1,
  "log_conformations": 1,
  "valence_size": 0.06,
  "surface_miserable": 2,
  "ray_opaque_background": -1,
  "transparency": 0,
  "ray_texture": 0,
  "ray_texture_settings": [0.1, 5, 1],
  "suspend_updates": 0,
  "full_screen": 0,
  "surface_mode": 0,
  "surface_color": { color: "-1" },
  "mesh_mode": 0,
  "mesh_color": { color: "-1" },
  "auto_indicate_flags": 0,
  "surface_debug": 0,
  "ray_improve_shadows": 0.1,
  "smooth_color_triangle": 0,
  "ray_default_renderer": 0,
  "field_of_view": 20,
  "reflect_power": 1,
  "preserve_chempy_ids": 0,
  "sphere_scale": 1,
  "two_sided_lighting": -1,
  "secondary_structure": 2,
  "auto_remove_hydrogens": 0,
  "raise_exceptions": 1,
  "stop_on_exceptions": 0,
  "sculpting": 0,
  "auto_sculpt": 0,
  "sculpt_vdw_scale": 0.97,
  "sculpt_vdw_scale14": 0.9,
  "sculpt_vdw_weight": 1,
  "sculpt_vdw_weight14": 0.2,
  "sculpt_bond_weight": 2.25,
  "sculpt_angl_weight": 1,
  "sculpt_pyra_weight": 1,
  "sculpt_plan_weight": 1,
  "sculpting_cycles": 10,
  "sphere_transparency": 0,
  "sphere_color": { color: "-1" },
  "sculpt_field_mask": 511,
  "sculpt_hb_overlap": 1,
  "sculpt_hb_overlap_base": 0.35,
  "legacy_vdw_radii": 0,
  "sculpt_memory": 1,
  "connect_mode": 0,
  "cartoon_cylindrical_helices": 0,
  "cartoon_helix_radius": 2.25,
  "connect_cutoff": 0.35,
  "save_pdb_ss": 0,
  "sculpt_line_weight": 1,
  "fit_iterations": 1000,
  "fit_tolerance": 1e-7,
  "batch_prefix": "tmp_pymol",
  "stereo_mode": 2,
  "cgo_sphere_quality": 1,
  "pdb_literal_names": 0,
  "wrap_output": 0,
  "fog_start": 0.45,
  "state": 1,
  "frame": 1,
  "ray_shadow": 1,
  "ribbon_trace_atoms": 0,
  "security": 1,
  "stick_transparency": 0,
  "ray_transparency_shadows": 1,
  "session_version_check": 0,
  "ray_transparency_specular": 0.6,
  "stereo_double_pump_mono": 0,
  "sphere_solvent": 0,
  "mesh_quality": 2,
  "mesh_solvent": 0,
  "dot_solvent": 0,
  "ray_shadow_fudge": 0.001,
  "ray_triangle_fudge": 1e-7,
  "debug_pick": 0,
  "dot_color": { color: "-1" },
  "mouse_limit": 100,
  "mouse_scale": 1.3,
  "transparency_mode": 2,
  "clamp_colors": 1,
  "pymol_space_max_red": 0.9,
  "pymol_space_max_green": 0.75,
  "pymol_space_max_blue": 0.9,
  "pymol_space_min_factor": 0.15,
  "roving_origin": 1,
  "roving_lines": 10,
  "roving_sticks": 6,
  "roving_spheres": 0,
  "roving_labels": 0,
  "roving_delay": 0.2,
  "roving_selection": "all",
  "roving_byres": 1,
  "roving_ribbon": -7,
  "roving_cartoon": 0,
  "roving_polar_contacts": 7,
  "roving_polar_cutoff": 3.31,
  "roving_nonbonded": 0,
  "float_labels": 0,
  "roving_detail": 0,
  "roving_nb_spheres": 8,
  "ribbon_color": { color: "-1" },
  "cartoon_color": { color: "-1" },
  "ribbon_smooth": 0,
  "auto_color": 1,
  "auto_color_next": 0,
  "ray_interior_color": { color: "-1" },
  "cartoon_highlight_color": { color: "-1" },
  "coulomb_units_factor": 557,
  "coulomb_dielectric": 2,
  "ray_interior_shadows": 0,
  "ray_interior_texture": -1,
  "roving_map1_name": "",
  "roving_map2_name": "",
  "roving_map3_name": "",
  "roving_map1_level": 1,
  "roving_map2_level": 2,
  "roving_map3_level": 3,
  "roving_isomesh": 8,
  "roving_isosurface": 0,
  "scenes_changed": 1,
  "gaussian_b_adjust": 0,
  "pdb_standard_order": 1,
  "cartoon_smooth_first": 1,
  "cartoon_smooth_last": 1,
  "cartoon_smooth_cycles": 2,
  "cartoon_flat_cycles": 4,
  "max_threads": 1,
  "show_progress": 1,
  "use_display_lists": 0,
  "cache_memory": 0,
  "simplify_display_lists": 0,
  "retain_order": 0,
  "pdb_hetatm_sort": 0,
  "pdb_use_ter_records": 1,
  "cartoon_trace_atoms": 0,
  "ray_oversample_cutoff": 120,
  "gaussian_resolution": 2,
  "gaussian_b_floor": 0,
  "sculpt_nb_interval": 17,
  "sculpt_tors_weight": 0.05,
  "sculpt_tors_tolerance": 0.05,
  "stick_ball": 0,
  "stick_ball_ratio": 1,
  "stick_fixed_radius": 0,
  "cartoon_transparency": 0,
  "dash_round_ends": 1,
  "h_bond_max_angle": 63,
  "h_bond_cutoff_center": 3.6,
  "h_bond_cutoff_edge": 3.2,
  "h_bond_power_a": 1.6,
  "h_bond_power_b": 5,
  "h_bond_cone": 180,
  "ss_helix_psi_target": -48,
  "ss_helix_psi_include": 55,
  "ss_helix_psi_exclude": 85,
  "ss_helix_phi_target": -57,
  "ss_helix_phi_include": 55,
  "ss_helix_phi_exclude": 85,
  "ss_strand_psi_target": 124,
  "ss_strand_psi_include": 40,
  "ss_strand_psi_exclude": 90,
  "ss_strand_phi_target": -129,
  "ss_strand_phi_include": 40,
  "ss_strand_phi_exclude": 100,
  "movie_loop": 1,
  "pdb_retain_ids": 0,
  "pdb_no_end_record": 0,
  "cgo_dot_width": 2,
  "cgo_dot_radius": -1,
  "defer_updates": 0,
  "normalize_o_maps": 1,
  "swap_dsn6_bytes": 1,
  "pdb_insertions_go_first": 0,
  "roving_origin_z": 1,
  "roving_origin_z_cushion": 3,
  "specular_intensity": 0.5,
  "overlay_lines": 5,
  "ray_transparency_spec_cut": 0.9,
  "internal_prompt": 1,
  "normalize_grd_maps": 0,
  "ray_blend_colors": 0,
  "ray_blend_red": 0.17,
  "ray_blend_green": 0.25,
  "ray_blend_blue": 0.14,
  "png_screen_gamma": 2.4,
  "png_file_gamma": 1,
  "editor_label_fragments": 0,
  "internal_gui_control_size": 18,
  "auto_dss": 1,
  "transparency_picking_mode": 2,
  "virtual_trackball": 1,
  "pdb_reformat_names_mode": 0,
  "ray_pixel_scale": 1.3,
  "label_font_id": 5,
  "pdb_conect_all": 0,
  "button_mode_name": "",
  "surface_type": 0,
  "dot_normals": 1,
  "session_migration": 1,
  "mesh_normals": 1,
  "mesh_type": 0,
  "dot_lighting": 1,
  "mesh_lighting": 0,
  "surface_solvent": 0,
  "triangle_max_passes": 5,
  "ray_interior_reflect": 0.4,
  "internal_gui_mode": 0,
  "surface_carve_selection": "",
  "surface_carve_state": 0,
  "surface_carve_cutoff": 0,
  "surface_clear_selection": "",
  "surface_clear_state": 0,
  "surface_clear_cutoff": 0,
  "surface_trim_cutoff": 0.2,
  "surface_trim_factor": 2,
  "ray_max_passes": 25,
  "active_selections": 1,
  "ray_transparency_contrast": 1,
  "seq_view": 0,
  "mouse_selection_mode": 1,
  "seq_view_label_spacing": 5,
  "seq_view_label_start": 1,
  "seq_view_format": 0,
  "seq_view_location": 0,
  "seq_view_overlay": 0,
  "auto_classify_atoms": 1,
  "cartoon_nucleic_acid_mode": 4,
  "seq_view_color": { color: "-1" },
  "seq_view_label_mode": 2,
  "surface_ramp_above_mode": 0,
  "stereo": 0,
  "wizard_prompt_mode": 1,
  "coulomb_cutoff": 10,
  "slice_track_camera": 0,
  "slice_height_scale": 1,
  "slice_height_map": 0,
  "slice_grid": 0.3,
  "slice_dynamic_grid": 0,
  "slice_dynamic_grid_resolution": 3,
  "pdb_insure_orthogonal": 1,
  "ray_direct_shade": 0,
  "stick_color": { color: "-1" },
  "cartoon_putty_radius": 0.4,
  "cartoon_putty_quality": -1,
  "cartoon_putty_scale_min": 0.6,
  "cartoon_putty_scale_max": 4,
  "cartoon_putty_scale_power": 1.5,
  "cartoon_putty_range": 2,
  "cartoon_side_chain_helper": 0,
  "surface_optimize_subsets": 1,
  "multiplex": -1,
  "texture_fonts": 0,
  "pqr_no_chain_id": 1,
  "animation": 1,
  "animation_duration": 0.75,
  "scene_animation": -1,
  "line_stick_helper": 1,
  "ray_orthoscopic": -1,
  "ribbon_side_chain_helper": 0,
  "selection_width_max": 10,
  "selection_width_scale": 2,
  "scene_current_name": "",
  "presentation": 0,
  "presentation_mode": 1,
  "pdb_truncate_residue_name": 0,
  "scene_loop": 0,
  "sweep_mode": 0,
  "sweep_phase": 0,
  "scene_restart_movie_delay": 1,
  "mouse_restart_movie_delay": 0,
  "angle_size": 0.6666,
  "angle_label_position": 0.5,
  "dihedral_size": 0.6666,
  "dihedral_label_position": 1.2,
  "defer_builds_mode": 0,
  "seq_view_discrete_by_state": 1,
  "scene_animation_duration": 2.25,
  "wildcard": "*",
  "atom_name_wildcard": "",
  "ignore_case": 1,
  "presentation_auto_quit": 1,
  "editor_auto_dihedral": 1,
  "presentation_auto_start": 1,
  "validate_object_names": 1,
  "unused_boolean_def_true": 1,
  "auto_show_spheres": 0,
  "sphere_mode": 9,
  "sphere_point_max_size": 18,
  "sphere_point_size": 1,
  "pdb_honor_model_number": 0,
  "rank_assisted_sorts": 1,
  "ribbon_nucleic_acid_mode": 0,
  "cartoon_ring_mode": 0,
  "cartoon_ring_width": 0.125,
  "cartoon_ring_color": { color: "-1" },
  "cartoon_ring_finder": 1,
  "cartoon_tube_cap": 2,
  "cartoon_loop_cap": 1,
  "nvidia_bugs": 0,
  "image_dots_per_inch": 0,
  "opaque_background": 0,
  "draw_frames": 0,
  "show_alpha_checker": 1,
  "matrix_mode": -1,
  "editor_auto_origin": 1,
  "session_file": "",
  "cgo_transparency": 0,
  "legacy_mouse_zoom": 0,
  "auto_number_selections": 0,
  "sculpt_vdw_vis_mode": 0,
  "sculpt_vdw_vis_min": -0.1,
  "sculpt_vdw_vis_mid": 0.1,
  "sculpt_vdw_vis_max": 0.3,
  "cartoon_ladder_mode": 1,
  "cartoon_ladder_radius": 0.25,
  "cartoon_ladder_color": { color: "-1" },
  "cartoon_nucleic_acid_color": { color: "-1" },
  "cartoon_ring_transparency": -1,
  "label_size": 14,
  "spec_direct": 0,
  "light_count": 2,
  "light2": [-0.55, -0.7, 0.15],
  "light3": [0.3, -0.6, -0.2],
  "hide_underscore_names": 1,
  "selection_round_points": 0,
  "distance_exclusion": 5,
  "h_bond_exclusion": 3,
  "label_shadow_mode": 0,
  "light4": [-1.2, 0.3, -0.2],
  "light5": [0.3, 0.6, -0.75],
  "light6": [-0.3, 0.5, 0],
  "light7": [0.9, -0.1, -0.15],
  "label_outline_color": { color: "-1" },
  "ray_trace_mode": 0,
  "ray_trace_gain": 0.12,
  "selection_visible_only": 0,
  "label_position": [0, 0, 1.75],
  "ray_trace_depth_factor": 0.1,
  "ray_trace_slope_factor": 0.6,
  "ray_trace_disco_factor": 0.05,
  "ray_shadow_decay_factor": 0,
  "ray_interior_mode": 0,
  "ray_legacy_lighting": 0,
  "sculpt_auto_center": 0,
  "pdb_discrete_chains": -1,
  "pdb_unbond_cations": 1,
  "sculpt_tri_scale": 1.025,
  "sculpt_tri_weight": 1,
  "sculpt_tri_min": 2,
  "sculpt_tri_max": 18,
  "sculpt_tri_mode": 0,
  "pdb_echo_tags": "HEADER, TITLE, COMPND",
  "connect_bonded": 0,
  "spec_direct_power": 55,
  "light8": [1.3, 2, 0.8],
  "light9": [-1.7, -0.5, 1.2],
  "ray_shadow_decay_range": 1.8,
  "spec_count": -1,
  "sculpt_min_scale": 0.975,
  "sculpt_min_weight": 0.75,
  "sculpt_min_min": 4,
  "sculpt_min_max": 12,
  "sculpt_max_scale": 1.025,
  "sculpt_max_weight": 0.75,
  "sculpt_max_min": 4,
  "sculpt_max_max": 12,
  "surface_circumscribe": -1,
  "sculpt_avd_weight": 4,
  "sculpt_avd_gap": -1,
  "sculpt_avd_range": -1,
  "sculpt_avd_excl": 7,
  "async_builds": 0,
  "fetch_path": ".",
  "cartoon_ring_radius": -1,
  "ray_color_ramps": 0,
  "ray_hint_camera": 2.15,
  "ray_hint_shadow": 0.65,
  "stick_valence_scale": 1,
  "seq_view_alignment": "",
  "seq_view_unaligned_mode": 0,
  "seq_view_unaligned_color": { color: "-1" },
  "seq_view_fill_char": "-",
  "seq_view_fill_color": { color: "104" },
  "seq_view_label_color": { color: "front" },
  "surface_carve_normal_cutoff": -1,
  "trace_atoms_mode": 5,
  "session_changed": 0,
  "ray_clip_shadows": 0,
  "mouse_wheel_scale": 0.5,
  "nonbonded_transparency": 0,
  "ray_spec_local": 0,
  "line_color": { color: "-1" },
  "ray_label_specular": 1,
  "mesh_skip": 0,
  "label_digits": 1,
  "label_distance_digits": -1,
  "label_angle_digits": -1,
  "label_dihedral_digits": -1,
  "surface_negative_visible": 0,
  "surface_negative_color": { color: "red" },
  "mesh_negative_visible": 0,
  "mesh_negative_color": { color: "red" },
  "group_auto_mode": 1,
  "group_full_member_names": 0,
  "gradient_max_length": 100,
  "gradient_min_length": 2,
  "gradient_min_slope": 0.00001,
  "gradient_normal_min_dot": 0.7,
  "gradient_step_size": 0.25,
  "gradient_spacing": 3,
  "gradient_symmetry": 0,
  "ray_trace_color": { color: "-6" },
  "group_arrow_prefix": 0,
  "suppress_hidden": 1,
  "session_compression": 0,
  "movie_fps": 30,
  "ray_transparency_oblique": 0,
  "ray_trace_trans_cutoff": 0.05,
  "ray_trace_persist_cutoff": 0.1,
  "ray_transparency_oblique_power": 4,
  "ray_scatter": 0,
  "h_bond_from_proton": 1,
  "auto_copy_images": 0,
  "moe_separate_chains": -1,
  "transparency_global_sort": 0,
  "hide_long_bonds": 0,
  "auto_rename_duplicate_objects": 0,
  "pdb_hetatm_guess_valences": 1,
  "ellipsoid_quality": 1,
  "cgo_ellipsoid_quality": -1,
  "movie_animate_by_frame": 0,
  "ramp_blend_nearby_colors": 0,
  "auto_defer_builds": 500,
  "ellipsoid_probability": 0.5,
  "ellipsoid_scale": 1,
  "ellipsoid_color": { color: "-1" },
  "ellipsoid_transparency": 0,
  "movie_rock": -1,
  "cache_mode": 0,
  "dash_color": { color: "-1" },
  "angle_color": { color: "-1" },
  "dihedral_color": { color: "-1" },
  "grid_mode": 0,
  "cache_max": 25000000,
  "grid_slot": -1,
  "grid_max": -1,
  "cartoon_putty_transform": 0,
  "rock": 0,
  "cone_quality": 18,
  "pdb_formal_charges": 1,
  "ati_bugs": 0,
  "geometry_export_mode": 0,
  "mouse_grid": 1,
  "mesh_cutoff": 0,
  "mesh_carve_selection": "",
  "mesh_carve_state": 0,
  "mesh_carve_cutoff": 0,
  "mesh_clear_selection": "",
  "mesh_clear_state": 0,
  "mesh_clear_cutoff": 0,
  "mesh_grid_max": 80,
  "session_cache_optimize": 0,
  "sdof_drag_scale": 0.5,
  "scene_buttons_mode": 1,
  "scene_buttons": 1,
  "map_auto_expand_sym": 1,
  "image_copy_always": 0,
  "max_ups": 0,
  "auto_overlay": 0,
  "stick_ball_color": { color: "-1" },
  "stick_h_scale": 0.4,
  "sculpt_pyra_inv_weight": 10,
  "keep_alive": 0,
  "fit_kabsch": 0,
  "stereo_dynamic_strength": 0.5,
  "dynamic_width": 1,
  "dynamic_width_factor": 0.06,
  "dynamic_width_min": 0.75,
  "dynamic_width_max": 2.5,
  "draw_mode": 0,
  "clean_electro_mode": 1,
  "valence_mode": 1,
  "show_frame_rate": 0,
  "movie_panel": 1,
  "mouse_z_scale": 1,
  "movie_auto_store": -1,
  "movie_auto_interpolate": 1,
  "movie_panel_row_height": 15,
  "scene_frame_mode": -1,
  "surface_cavity_mode": 0,
  "surface_cavity_radius": 7,
  "surface_cavity_cutoff": -3,
  "motion_power": 0,
  "motion_bias": -1,
  "motion_simple": 0,
  "motion_linear": 0,
  "motion_hand": 1,
  "pdb_ignore_conect": 0,
  "editor_bond_cycle_mode": 1,
  "movie_quality": 90,
  "label_anchor": "CA",
  "fetch_host": "pdb",
  "dynamic_measures": 1,
  "neighbor_cutoff": 3.5,
  "heavy_neighbor_cutoff": 3.5,
  "polar_neighbor_cutoff": 3.5,
  "surface_residue_cutoff": 2.5,
  "surface_use_shader": 1,
  "cartoon_use_shader": 1,
  "stick_use_shader": 1,
  "line_use_shader": 1,
  "sphere_use_shader": 1,
  "use_shaders": 0,
  "shaders_from_disk": 0,
  "volume_bit_depth": 16,
  "volume_color": { color: "-1" },
  "volume_layers": 256,
  "volume_data_range": 5,
  "auto_defer_atom_count": 0,
  "default_refmac_names": "FWT PHWT DELFWT PHDELWT",
  "default_phenix_names": "2FOFCWT PH2FOFCWT FOFCWT PHFOFCWT",
  "default_phenix_no_fill_names": "2FOFCWT_no_fill PH2FOFCWT_no_fill None None",
  "default_buster_names": "2FOFCWT PH2FOFCWT FOFCWT PHFOFCWT",
  "default_fofc_map_rep": "volume",
  "default_2fofc_map_rep": "volume",
  "atom_type_format": "mol2",
  "autoclose_dialogs": 1,
  "bg_gradient": 0,
  "bg_rgb_top": { color: "0x00004D" },
  "bg_rgb_bottom": { color: "0x333380" },
  "ray_volume": 0,
  "ribbon_transparency": 0,
  "state_counter_mode": -1,
  "cgo_use_shader": 1,
  "cgo_shader_ub_color": 0,
  "cgo_shader_ub_normal": 0,
  "cgo_lighting": 1,
  "mesh_use_shader": 1,
  "stick_debug": 0,
  "cgo_debug": 0,
  "stick_round_nub": 0,
  "stick_good_geometry": 0,
  "stick_as_cylinders": 1,
  "mesh_as_cylinders": 0,
  "line_as_cylinders": 0,
  "ribbon_as_cylinders": 0,
  "ribbon_use_shader": 1,
  "excl_display_lists_shaders": 0,
  "dash_use_shader": 1,
  "dash_as_cylinders": 1,
  "nonbonded_use_shader": 1,
  "nonbonded_as_cylinders": 0,
  "cylinders_shader_filter_faces": 1,
  "nb_spheres_size": 0.25,
  "nb_spheres_quality": 1,
  "nb_spheres_use_shader": 1,
  "render_as_cylinders": 1,
  "alignment_as_cylinders": 0,
  "cartoon_nucleic_acid_as_cylinders": 1,
  "cgo_shader_ub_flags": 0,
  "antialias_shader": 0,
  "offscreen_rendering_multiplier": 4,
  "cylinder_shader_ff_workaround": 1,
  "surface_color_smoothing": 1,
  "surface_color_smoothing_threshold": 0.05,
  "dot_use_shader": 1,
  "dot_as_spheres": 0,
  "ambient_occlusion_mode": 0,
  "ambient_occlusion_scale": 25,
  "ambient_occlusion_smooth": 10,
  "smooth_half_bonds": 1,
  "anaglyph_mode": 4,
  "edit_light": 1,
  "suspend_undo": 0,
  "suspend_undo_atom_count": 1000,
  "suspend_deferred": 0,
  "pick_surface": 0,
  "bg_image_filename": "",
  "bg_image_mode": 0,
  "bg_image_tilesize": [100, 100, 0],
  "bg_image_linear": 1,
  "load_object_props_default": "*",
  "load_atom_props_default": "*",
  "label_placement_offset": [0, 0, 0],
  "pdb_conect_nodup": 1,
  "label_connector": 0,
  "label_connector_mode": 0,
  "label_connector_color": { color: "-6" },
  "label_connector_width": 2,
  "label_connector_ext_length": 2.5,
  "label_bg_color": { color: "-1" },
  "use_geometry_shaders": 1,
  "label_relative_mode": 0,
  "label_screen_point": [0, 0, 0],
  "label_multiline_spacing": 1.2,
  "label_multiline_justification": 1,
  "label_padding": [0.2, 0.2, 0],
  "label_bg_transparency": 0.6,
  "label_bg_outline": 0,
  "ray_label_connector_flat": 1,
  "dash_transparency": 0,
  "pick_labels": 1,
  "label_z_target": 0,
  "session_embeds_data": 1,
  "volume_mode": 0,
  "trilines": 0,
  "collada_export_lighting": 0,
  "collada_geometry_mode": 1,
  "precomputed_lighting": 0,
  "chromadepth": 0,
  "pse_export_version": 0,
  "cif_use_auth": 1,
  "assembly": "",
  "cif_keepinmemory": 0,
  "pse_binary_dump": 0,
  "cartoon_gap_cutoff": 10,
  "ignore_case_chain": 0,
  "valence_zero_scale": 0.2,
  "valence_zero_mode": 1,
  "auto_show_classified": -1,
  "collada_background_box": 0,
  "pick32bit": 1,
  "cartoon_all_alt": 0,
  "display_scale_factor": 1,
  "pick_shading": 0,
  "fetch_type_default": "cif",
  "editor_auto_measure": 1,
  "surface_smooth_edges": 1,
  "chem_comp_cartn_use": 0,
  "colored_feedback": 0,
  "sdf_write_zero_order_bonds": 0,
  "cif_metalc_as_zero_order_bonds": 0,
  "seq_view_gap_mode": 1,
  "internal_gui_name_color_mode": 0,
  "openvr_gui_fov": 35,
  "openvr_gui_alpha": 1,
  "openvr_gui_use_alpha": 0,
  "openvr_gui_scene_color": 0,
  "openvr_gui_scene_alpha": 0.75,
  "openvr_gui_back_color": 0.2,
  "openvr_gui_back_alpha": 0.75,
  "openvr_gui_use_backdrop": 0,
  "openvr_gui_overlay": 0,
  "openvr_gui_text": 0,
  "openvr_disable_clipping": 0,
  "openvr_near_plane": 0.1,
  "openvr_far_plane": 100,
  "openvr_cut_laser": 0,
  "openvr_laser_width": 3,
  "openvr_gui_distance": 1.5,
  "cartoon_smooth_cylinder_cycles": 3,
  "cartoon_smooth_cylinder_window": 2,
  "isosurface_algorithm": 0,
  "cell_centered": 0,
  "halogen_bond_distance": 3.5,
  "halogen_bond_as_donor_min_donor_angle": 140,
  "halogen_bond_as_donor_min_acceptor_angle": 90,
  "halogen_bond_as_acceptor_min_donor_angle": 120,
  "halogen_bond_as_acceptor_min_acceptor_angle": 90,
  "halogen_bond_as_acceptor_max_acceptor_angle": 170,
  "salt_bridge_distance": 5,
  "use_tessellation_shaders": 1,
  "cell_color": { color: "-1" },
};
